/**
 * tdesign v1.1.1
 * (c) 2023 tdesign
 * @license MIT
 */

import { _ as _defineProperty } from '../_chunks/dep-8c8a3203.js';
import { _ as _objectWithoutProperties } from '../_chunks/dep-97bb7f6b.js';
import React, { forwardRef, useContext } from 'react';
import classNames from 'classnames';
import { i as isObject_1 } from '../_chunks/dep-73c3cd54.js';
import useConfig from '../hooks/useConfig.js';
import { colDefaultProps } from './defaultProps.js';
import { RowContext } from './Row.js';
import '../_chunks/dep-2055661d.js';
import '../_chunks/dep-8670d51c.js';
import '../config-provider/ConfigContext.js';
import '../_chunks/dep-1d5f47c2.js';
import '../_chunks/dep-df9f8f04.js';
import '../_chunks/dep-9d326786.js';
import '../_chunks/dep-3542b5f1.js';
import '../_chunks/dep-79aca757.js';
import '../_chunks/dep-c7d784af.js';
import '../_chunks/dep-ced835e2.js';
import '../_chunks/dep-1e76a561.js';
import '../_chunks/dep-6b33e1e9.js';
import '../_chunks/dep-dae4a585.js';
import '../_chunks/dep-9e05eaf2.js';
import '../_chunks/dep-0d617aaa.js';
import '../_chunks/dep-030d278c.js';
import '../_chunks/dep-868937ef.js';
import '../_chunks/dep-6f5c07a7.js';
import '../_chunks/dep-a79a1775.js';
import '../_chunks/dep-7b744c82.js';
import '../_chunks/dep-0b20126b.js';
import '../_chunks/dep-2b7035c1.js';
import '../locale/zh_CN.js';
import '../_common/js/global-config/locale/zh_CN.js';
import 'dayjs';
import '../_chunks/dep-4fce6f2f.js';
import '../_common/js/global-config/default-config.js';
import '../_chunks/dep-605e961c.js';
import '../_chunks/dep-f66447d3.js';
import '../_util/dom.js';
import 'raf';
import '../_chunks/dep-378695bd.js';
import '../_util/easing.js';

var _excluded = ["flex", "offset", "order", "pull", "push", "span", "tag", "className", "children", "style"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var calcColPadding = function calcColPadding(gutter, currentSize) {
  var paddingObj = {};
  if (typeof gutter === "number") {
    Object.assign(paddingObj, {
      paddingLeft: "".concat(gutter / 2, "px"),
      paddingRight: "".concat(gutter / 2, "px")
    });
  } else if (Array.isArray(gutter) && gutter.length) {
    if (typeof gutter[0] === "number") {
      Object.assign(paddingObj, {
        paddingLeft: "".concat(gutter[0] / 2, "px"),
        paddingRight: "".concat(gutter[0] / 2, "px")
      });
    }
    if (isObject_1(gutter[0]) && gutter[0][currentSize]) {
      Object.assign(paddingObj, {
        paddingLeft: "".concat(gutter[0][currentSize] / 2, "px"),
        paddingRight: "".concat(gutter[0][currentSize] / 2, "px")
      });
    }
  } else if (isObject_1(gutter) && gutter[currentSize]) {
    Object.assign(paddingObj, {
      paddingLeft: "".concat(gutter[currentSize] / 2, "px"),
      paddingRight: "".concat(gutter[currentSize] / 2, "px")
    });
  }
  return paddingObj;
};
var parseFlex = function parseFlex(flex) {
  if (typeof flex === "number") {
    return "".concat(flex, " ").concat(flex, " auto");
  }
  if (/^\d+(\.\d+)?(px|r?em|%)$/.test(flex)) {
    return "0 0 ".concat(flex);
  }
  return flex;
};
var Col = /*#__PURE__*/forwardRef(function (props, ref) {
  var _classNames;
  var flex = props.flex,
    offset = props.offset,
    order = props.order,
    pull = props.pull,
    push = props.push,
    span = props.span,
    tag = props.tag,
    className = props.className,
    children = props.children,
    propStyle = props.style,
    otherColProps = _objectWithoutProperties(props, _excluded);
  var _useContext = useContext(RowContext),
    rowGutter = _useContext.gutter,
    rowSize = _useContext.size;
  var _useConfig = useConfig(),
    classPrefix = _useConfig.classPrefix;
  var allSizes = ["xs", "sm", "md", "lg", "xl", "xxl"];
  var sizeClasses = allSizes.reduce(function (acc, currSize) {
    var _objectSpread2;
    var sizeProp = props[currSize];
    var sizeObj = {};
    if (typeof sizeProp === "number") {
      sizeObj.span = sizeProp;
    } else if (isObject_1(sizeProp)) {
      sizeObj = sizeProp || {};
    }
    return _objectSpread(_objectSpread({}, acc), {}, (_objectSpread2 = {}, _defineProperty(_objectSpread2, "".concat(classPrefix, "-col-").concat(currSize, "-").concat(sizeObj.span), sizeObj.span !== void 0), _defineProperty(_objectSpread2, "".concat(classPrefix, "-col-").concat(currSize, "-order-").concat(sizeObj.order), parseInt(sizeObj.order, 10) >= 0), _defineProperty(_objectSpread2, "".concat(classPrefix, "-col-").concat(currSize, "-offset-").concat(sizeObj.offset), parseInt(sizeObj.offset, 10) >= 0), _defineProperty(_objectSpread2, "".concat(classPrefix, "-col-").concat(currSize, "-push-").concat(sizeObj.push), parseInt(sizeObj.push, 10) >= 0), _defineProperty(_objectSpread2, "".concat(classPrefix, "-col-").concat(currSize, "-pull-").concat(sizeObj.pull), parseInt(sizeObj.pull, 10) >= 0), _objectSpread2));
  }, {});
  var colClassNames = classNames("".concat(classPrefix, "-col"), className, (_classNames = {}, _defineProperty(_classNames, "".concat(classPrefix, "-col-").concat(span), span !== void 0), _defineProperty(_classNames, "".concat(classPrefix, "-col-offset-").concat(offset), parseInt(offset, 10) >= 0), _defineProperty(_classNames, "".concat(classPrefix, "-col-pull-").concat(pull), parseInt(pull, 10) >= 0), _defineProperty(_classNames, "".concat(classPrefix, "-col-push-").concat(push), parseInt(push, 10) >= 0), _defineProperty(_classNames, "".concat(classPrefix, "-col-order-").concat(order), parseInt(order, 10) >= 0), _classNames), sizeClasses);
  var colStyle = _objectSpread(_objectSpread({}, calcColPadding(rowGutter, rowSize)), propStyle);
  flex && (colStyle.flex = parseFlex(flex));
  return /*#__PURE__*/React.createElement(tag, _objectSpread({
    ref: ref,
    className: colClassNames,
    style: colStyle
  }, otherColProps), children);
});
Col.displayName = "Col";
Col.defaultProps = colDefaultProps;

export { Col as default };
//# sourceMappingURL=Col.js.map
